// 統一的 API 請求封裝
// 後端以 body.code 表示業務狀態（HTTP 多為 200），此處統一攤平：
// code === 200 → 回傳 data ； 其餘 → 丟出 Error（message 為後端 msg，code 為後端 code）
// 帶上後端的業務代碼：呼叫端有時要依代碼決定下一步（例如 428 個人資料未填齊就導去會員中心）
function bizError(res) {
  const err = new Error(res?.msg || '請求失敗')
  err.code = res?.code
  return err
}

// 登入流程自己的端點：平台清單、授權網址、第三方回調、換發。
// 這幾支後端都不看 Authorization，前台也不該為它們動用 cookie 裡的憑證——
// 換帳號登入時那組憑證還是上一個帳號的，拿它去換一張新的 access token 會把剛登入的
// 身分蓋掉（踩過：按下 Google 登入時用殘留的 LINE refresh token 換發，
// 登入完成後「我的帳號」顯示的是 LINE 帳號）
const AUTH_FLOW_URL = /^\/front\/auth\/(refresh|providers|[^/]+\/(url|callback))(\?|$)/

function isAuthFlow(url) {
  return AUTH_FLOW_URL.test(url)
}

export function useApi() {
  const nuxtApp = useNuxtApp()
  const config = useRuntimeConfig()
  const token = useCookie('web_token', { maxAge: 60 * 60 * 24 * 7, sameSite: 'lax' })
  const refreshToken = useCookie('web_refresh_token', { maxAge: 60 * 60 * 24 * 7, sameSite: 'lax' })

  // SSR 期間走內網位址（NUXT_API_INTERNAL_BASE），瀏覽器端走公開位址（NUXT_PUBLIC_API_BASE）
  const baseURL = import.meta.server ? config.apiInternalBase : config.public.apiBase

  // 後端服務根位址（health 等非 /api 前綴的介面用）
  const serverBase = baseURL.replace(/\/api\/?$/, '')

  function authHeaders(extra = {}) {
    return token.value ? { Authorization: `Bearer ${token.value}`, ...extra } : { ...extra }
  }

  /**
   * access token 過期了沒
   *
   * 只讀 payload 的 exp，不驗簽章（驗簽是後端的事），解不出來就當作沒過期、交給後端判斷。
   * 提前 30 秒換發，免得請求剛好卡在過期邊界上。
   */
  function isExpired(jwtToken) {
    try {
      const b64 = jwtToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
      const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)))
      return typeof payload.exp === 'number' && payload.exp * 1000 - Date.now() < 30000
    } catch {
      return false
    }
  }

  // 以原始 $fetch 換發 token，避免遞迴進本攔截流程
  async function doRefresh() {
    if (!refreshToken.value) throw new Error('登入已失效，請重新登入')
    const res = await $fetch('/front/auth/refresh', {
      baseURL,
      method: 'POST',
      body: { refreshToken: refreshToken.value }
    })
    if (res?.code !== 200) throw new Error(res?.msg || '登入已失效，請重新登入')
    token.value = res.data.token
    return res.data.token
  }

  // 登入狀態的唯一依據是 web_token（useAuth 的 isLogged 看的就是它），
  // 清掉之後頁首會自己換回「網路票選登入」
  function clearAuth() {
    token.value = null
    refreshToken.value = null
  }

  // 一個頁面同時打好幾支介面時只換發一次。
  // 狀態掛在 nuxtApp 上而不是模組變數：SSR 期間模組是所有使用者共用的，
  // 換發狀態混在一起會把別人的 token 塞給另一個人
  function refreshOnce() {
    nuxtApp._frontTokenRefresh ??= doRefresh().finally(() => {
      nuxtApp._frontTokenRefresh = null
    })
    return nuxtApp._frontTokenRefresh
  }

  /**
   * 送出前先確認 access token 還有效，過期就先換發
   *
   * 需要登入的端點過期會回 401，由下面的重試流程處理；但 `participantOptional` 的端點
   * （投票頁列表、步道詳情、蓋章頁）拿到過期 token 只會「當作沒登入」而不回 401——
   * 畫面於是變成四區都還沒投票、頁首卻還顯示「我的帳號」。所以這裡自己先看一眼。
   * 換不動（refreshToken 也過期了）就清掉登入狀態，當作未登入把頁面照常渲染出來。
   */
  async function ensureFreshToken(url) {
    if (!token.value || isAuthFlow(url)) return
    if (!isExpired(token.value)) return
    if (!refreshToken.value) return clearAuth()

    try {
      await refreshOnce()
    } catch {
      clearAuth()
    }
  }

  async function request(url, options = {}) {
    await ensureFreshToken(url)

    const send = () =>
      $fetch(url, {
        baseURL,
        ...options,
        headers: authHeaders(options.headers)
      })

    let res
    try {
      res = await send()
    } catch (err) {
      // 網路層／HTTP 層錯誤
      throw new Error(err?.data?.msg || err?.message || '網路異常')
    }

    if (res?.code === 200) return res.data

    // token 過期 → 換發後重試一次
    if (res?.code === 401 && refreshToken.value && !isAuthFlow(url)) {
      await doRefresh()
      const retry = await send()
      if (retry?.code === 200) return retry.data
      throw bizError(retry)
    }

    throw bizError(res)
  }

  return {
    // token 與 clearAuth 一併對外：useAuth 不要自己再 useCookie 一次。
    // Nuxt 的 useCookie 在瀏覽器端每呼叫一次就重讀 document.cookie 建一個新 ref，
    // 兩份 ref 只靠 BroadcastChannel 非同步互通，登入這種密集讀寫的時刻誰最後寫進
    // cookie 是時序決定的，共用一份才不會互相蓋掉
    token,
    refreshToken,
    clearAuth,
    baseURL,
    serverBase,
    request,
    get: (url, params, options = {}) => request(url, { method: 'GET', query: params, ...options }),
    post: (url, body, options = {}) => request(url, { method: 'POST', body, ...options }),
    put: (url, body, options = {}) => request(url, { method: 'PUT', body, ...options }),
    del: (url, options = {}) => request(url, { method: 'DELETE', ...options })
  }
}
